import { handleError } from '@zyno-io/vue-foundation';
import { ref } from 'vue';

import { Logger } from '@/shared/services/logger';

interface IMessageEvent {
    data: string;
}

interface IEmbeddedWindow {
    chrome: {
        webview: {
            postMessage: (message: string) => void;
            addEventListener: (e: 'message', listener: (event: IMessageEvent) => void) => void;
            hostObjects: {
                zwBridge: {
                    isDevelopment: () => Promise<boolean>;
                    generateHandshake: () => Promise<string>;
                    launchRegistration: () => Promise<void>;
                    performPostRegistrationTasks: () => Promise<void>;
                    hasFingerprintReader: () => Promise<boolean>;
                };
            };
        };
    };
}

export interface IInboundEventPayloads {
    fingerprintReaderConnected: undefined;
    fingerprintReaderDisconnected: undefined;
    cardReaderConnected: undefined;
    cardReaderDisconnected: undefined;
    scaleConnected: undefined;
    scaleDisconnected: undefined;
    fingerprint: { Png: string; Data: string };
}

declare global {
    interface Window {
        chrome: {
            webview: {
                postMessage: (message: string) => void;
                addEventListener: (e: 'message', listener: (event: IMessageEvent) => void) => void;
                hostObjects: {
                    zwBridge: {
                        isDevelopment: () => Promise<boolean>;
                        generateHandshake: () => Promise<string>;
                        launchRegistration: () => Promise<void>;
                        performPostRegistrationTasks: () => Promise<void>;
                        hasFingerprintReader: () => Promise<boolean>;
                    };
                };
            };
        };
    }
}

const logger = Logger.createScoped('WorkstationBridge');

export class WorkstationBridge {
    private static isDevelopment = false;
    private static embeddedWindow?: IEmbeddedWindow;
    // eslint-disable-next-line no-explicit-any
    private static listeners: { [key: string]: ((data: any) => void)[] } = {};
    private static lastMessageEvent?: IMessageEvent;
    static devices = ref({
        fingerprintReader: false,
        cardReader: false,
        scale: false
    });

    static isWithinZynoWorkstation() {
        try {
            window.chrome.webview.hostObjects.zwBridge.isDevelopment();
            return true;
        } catch {
            return false;
        }
    }

    static async init() {
        if (this.isWithinZynoWorkstation()) {
            this.embeddedWindow = window as unknown as IEmbeddedWindow;
            this.embeddedWindow.chrome.webview.addEventListener('message', e => {
                // unclear why we're getting the event multiple times. happened after adding a service worker.
                if (this.lastMessageEvent === e) return;
                this.lastMessageEvent = e;
                this.handleMessage(e.data);
            });
            this.isDevelopment = await this.embeddedWindow.chrome.webview.hostObjects.zwBridge.isDevelopment();

            this.addEventListener('fingerprintReaderConnected', () => (this.devices.value.fingerprintReader = true));
            this.addEventListener('fingerprintReaderDisconnected', () => (this.devices.value.fingerprintReader = false));
            this.addEventListener('cardReaderConnected', () => (this.devices.value.cardReader = true));
            this.addEventListener('cardReaderDisconnected', () => (this.devices.value.cardReader = false));
            this.addEventListener('scaleConnected', () => (this.devices.value.scale = true));
            this.addEventListener('scaleDisconnected', () => (this.devices.value.scale = false));

            this.bridgeFunctions?.hasFingerprintReader().then(hasReader => {
                this.devices.value.fingerprintReader = hasReader;
            });
        }
    }

    static get bridgeFunctions(): IEmbeddedWindow['chrome']['webview']['hostObjects']['zwBridge'] | undefined {
        if (this.isDevelopment) {
            // eslint-disable-next-line no-explicit-any
            return new Proxy({} as any, {
                get: (_target, prop) => {
                    // eslint-disable-next-line no-explicit-any
                    return async (...args: any[]) => {
                        logger.info('invoking:', prop, args);
                        const bridge = this.embeddedWindow?.chrome.webview.hostObjects.zwBridge;
                        // eslint-disable-next-line no-explicit-any
                        const result = await (bridge as any)?.[prop](...args);
                        logger.info(`invocation result [${String(prop)}]:`, result);
                        return result;
                    };
                }
            });
        }
        return this.embeddedWindow?.chrome.webview.hostObjects.zwBridge;
    }

    private static handleMessage(message: string) {
        if (this.isDevelopment) logger.info('received message:', message);
        try {
            const { Event, Data } = JSON.parse(message);
            this.listeners[Event]?.forEach(listener => listener(Data));
        } catch {
            handleError('failed to parse message', message);
        }
    }

    static addEventListener<T extends keyof IInboundEventPayloads>(e: T, listener: (data: IInboundEventPayloads[T]) => void) {
        if (!this.listeners[e]) {
            this.listeners[e] = [];
        }
        this.listeners[e].push(listener);
    }

    static removeEventListener<T extends keyof IInboundEventPayloads>(e: T, listener: (data: IInboundEventPayloads[T]) => void) {
        if (!this.listeners[e]) {
            return;
        }
        this.listeners[e] = this.listeners[e].filter(l => l !== listener);
    }

    static postMessage(message: string) {
        if (this.isDevelopment) logger.info('posting message:', message);
        this.embeddedWindow?.chrome.webview.postMessage(message);
    }
}

if (import.meta.env.VITE_APP_ENABLE_FAKE_WORKSTATION_BRIDGE && !('chrome' in window && 'webview' in window.chrome)) {
    const eventListeners: ((event: { data: string }) => void)[] = [];

    window.chrome = {
        webview: {
            postMessage() {},

            addEventListener(_e: 'message', listener: (event: { data: string }) => void) {
                eventListeners.push(listener);
            },

            hostObjects: {
                zwBridge: {
                    async isDevelopment() {
                        return true;
                    },

                    async generateHandshake() {
                        return prompt('Enter handshake string') as string;
                    },

                    async launchRegistration() {
                        logger.info('launchRegistration');
                    },

                    async performPostRegistrationTasks() {
                        logger.info('performPostRegistrationTasks');
                    },

                    async hasFingerprintReader() {
                        return false;
                    }
                }
            }
        }
    };
}
